import { Button, Checkbox, ConfigProvider, DatePicker, Form, Input, message, Modal, Select, TreeDataNode, TreeSelect } from "antd";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import container from "@/lib/di/container";
import React from "react";
import { MODAL_THEME } from "@/core/themes/modal.theme";
import { AppUserRepo } from "@/core/repos/tables/app-user.repo";
import { AppUserModel } from "@/core/models/app-user.model";
import { dayjsPipe } from "@/core/pipes/dayjs.pipe";
import { usePermissionStore } from "@/core/stores/permission.store";
import { SaveButton } from "@/app/home/components/table/SaveButton";
import { Province } from "@/core/models/address/province.model";
import { District } from "@/core/models/address/district.model";
import { Ward } from "@/core/models/address/ward.model";
import { TollAreaModel } from "@/core/models/toll-area/toll-area.model";
import { AddressRepo } from "@/core/repos/address.repo";
import { TollAreaRepo } from "@/core/repos/tables/toll-area.repo";
import { AppUserAreaType } from "@/core/enums/global.enums";
import { DepartmentRepo } from "@/core/repos/tables/department.repo";
import { DepartmentModel } from "@/core/models/department.model";
import { SyncOutlined } from "@ant-design/icons";
import { normalizeVietnameseString } from "@/core/pipes/norm-vn.pipe";
import { DEF_DATE_FORMAT, GlobalUtils } from "@/app/home/utils/global.utils";

const USERNAME_EDIT_ALLOWED = process.env.REACT_APP_ACCOUNT_USERNAME_ALLOW_EDIT === 'true';

const appUserRepo = container.get(AppUserRepo);
const addressRepo = container.get(AddressRepo);
const tollAreaRepo = container.get(TollAreaRepo);
const departmentRepo = container.get(DepartmentRepo);

interface CreateModalProps {
    isModalOpen: boolean,
    setIsModalOpen: Dispatch<SetStateAction<boolean>>,
    data: AppUserModel,
}

interface TreeSelectDataNode {
    value: string,
    label: string,
    children?: TreeSelectDataNode[]
}

const INPUT_WIDTH = 450;

export const DetailModal: React.FC<CreateModalProps> = ({isModalOpen, setIsModalOpen, data}) => {
    const permissionStore = usePermissionStore();
    const [form] = Form.useForm<AppUserModel>();
    const [isEdit, setIsEdit] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [areaTreeNodes, setAreaTreeNodes] = useState<TreeSelectDataNode[]>([])
    const [selectedAreas, setSelectedAreas] = useState<string[]>([]);
    const [provinces, setProvinces] = useState<Province[]>([]);
    const [districts, setDistricts] = useState<District[]>([]);
    const [wards, setWards] = useState<Ward[]>([]);
    const [tollAreas, setTollAreas] = useState<TollAreaModel[]>([]);
    const [departments, setDepartments] = useState<DepartmentModel[]>([]);

    //Attach hooks to handle data changes
    const handleDataChange = async () => {
        const isEditing = data.id&&data.id.length>0;

        if (typeof data.areas === 'string') {
            const areas = GlobalUtils.parseArray(data.areas);
            setSelectedAreas(areas);
        } else {
            const defaultAreas = process.env.REACT_APP_DEFAULT_AREA;
            if (typeof defaultAreas === 'string'&&!isEditing) {
                const areas = GlobalUtils.parseArray(defaultAreas);
                setSelectedAreas(areas);
            } else {
                setSelectedAreas([]);
            }
        }
        form.setFieldsValue(data);
        if (isEditing) {
            setIsEdit(true);
        } else {
            setIsEdit(false);
        }
    }
    useEffect(()=>{
        handleDataChange();
    }, [data])

    const init = async () => {
        await renderAreaTree();
        const _departments = await departmentRepo.getAll();
        setDepartments(_departments);
    }

    const renderAreaTree = async () => {
        const provinces = (await addressRepo.getAllProvinces()).map(p => p[1]);
        const districts = (await addressRepo.getAllDistricts()).map(d => d[1]);
        const wards = (await addressRepo.getAllWards()).map(w => w[1]);
        const tollAreas = await tollAreaRepo.getAll();
        setProvinces(provinces);
        setDistricts(districts);
        setWards(wards);
        setTollAreas(tollAreas);
        generateAreaTree(provinces, districts, wards, tollAreas);

    }

    const generateAreaTree = (provinces: Province[], districts: District[], wards: Ward[], tollAreas: TollAreaModel[]) => {
        const treeData: TreeSelectDataNode[] = [];
        provinces.forEach((province: Province) => {
            treeData.push({
                label: province.name,
                value: `${AppUserAreaType.Province}_${province.id}`,
                children: [
                    {
                        label: "Tất cả Huyện trực thuộc",
                        value: `All_${AppUserAreaType.Province}_${province.id}`,
                    },
                    ...districts
                        .filter(d => d.provinceId === province.id)
                        .map((district: District): TreeSelectDataNode => ({
                            label: district.name,
                            value: `${AppUserAreaType.District}_${district.id}`,
                            children: [
                                {
                                    label: "Tất cả Xã trực thuộc",
                                    value: `All_${AppUserAreaType.District}_${district.id}`,
                                },
                                ...wards
                                    .filter(w => w.districtId === district.id)
                                    .map((ward: Ward): TreeSelectDataNode => ({
                                        label: ward.name,
                                        value: `${AppUserAreaType.Ward}_${ward.id}`,
                                        children: [
                                            {
                                                label: "Tất cả Khu vực trực thuộc",
                                                value: `All_${AppUserAreaType.Ward}_${ward.id}`,
                                            },
                                            ...tollAreas
                                                .filter(t => t.wardId === ward.id)
                                                .map((tollArea: TollAreaModel): TreeSelectDataNode => ({
                                                    label: tollArea.name,
                                                    value: `${AppUserAreaType.TollArea}_${tollArea.id}`
                                                }))
                                        ]
                                    }))
                            ]
                        }))
                ]
            })
        });
        setAreaTreeNodes(treeData);
    }

    //componentDidMount equivalent, run exclusively on client-side
    useEffect(() => {init()}, []);

    const handleSave = async () => {
        try {
            setLoading(true);
            const formData = await form.validateFields();
            if (selectedAreas&&selectedAreas.length>0) {
                formData.areas = selectedAreas.join(";")+";"
            } else {
                formData.areas = "";
            }
            if (isEdit) {
                formData.id = data.id;
                const result = await appUserRepo.update({...data, ...formData});
                setIsModalOpen(false);
                if (result) {
                    message.success({
                        content: "Đã cập nhật"
                    })
                } else {
                    message.error({
                        content: "Cập nhật thất bại"
                    })
                }
            } else {
                const result = await appUserRepo.create({...data, ...formData});
                setIsModalOpen(false);
                if (result) {
                    message.success({
                        content: "Thêm bản ghi mới thành công"
                    })
                } else {
                    message.error({
                        content: "Thêm bản ghi mới thất bại"
                    })
                }
            }
        } catch (error) {
            console.log(error);
        }
        setLoading(false);
    };
  
    const handleCancel = () => {
      setIsModalOpen(false);
    };
    
    const validatePasswords = (_: any, value: string): Promise<void> => {
        if (value && value !== form.getFieldValue('passwordHash')) {
          return Promise.reject(new Error('Mật khẩu không giống nhau!'));
        }
        return Promise.resolve();
    };

    const onSelectedAreasChange = (selected: string[]) => {
        const _selectedAreas = Array.from(new Set([
            ...selected.filter(s => !(s.split("_")[0] === "All")),
            ...selected.filter(s => s.split("_")[0] === "All").map(s => s.split("_").slice(1).join("_"))
        ]));
        const selectedProvinces = _selectedAreas
        .filter(area => area.split("_")[0] === AppUserAreaType.Province)
        .map(area => area.split("_")[1]);
        const selectedDistricts = _selectedAreas
            .filter(area => area.split("_")[0] === AppUserAreaType.District)
            .map(area => area.split("_")[1]);
        const selectedWards = _selectedAreas
            .filter(area => area.split("_")[0] === AppUserAreaType.Ward)
            .map(area => area.split("_")[1]);
        const includedDistrict = districts
            .filter(districts => selectedProvinces.includes(districts.provinceId))
            .map(district => `${AppUserAreaType.District}_${district.id}`);
        const includedWards = wards.filter(ward => selectedDistricts.includes(ward.districtId))
            .map(ward => `${AppUserAreaType.Ward}_${ward.id}`);
        const includedTollAreas = tollAreas
            .filter(tollArea => 
                selectedProvinces.includes(tollArea.provinceId)
                || selectedDistricts.includes(tollArea.districtId)
                || selectedWards.includes(tollArea.wardId)
            )
            .map(tollArea => `${AppUserAreaType.TollArea}_${tollArea.id}`);
        const includedAreas = [...includedDistrict, ...includedWards, ...includedTollAreas];
        const filteredSelectedAreas = _selectedAreas.filter(area => !includedAreas.includes(area));
        console.log(filteredSelectedAreas)
        setSelectedAreas(filteredSelectedAreas);
    }

    const generateUsername = async () => {
        const departmentId = form.getFieldValue("departmentId");
        if (departmentId) {
            const userName = await appUserRepo.generateUsernameByDepartmentId(departmentId);
            if (userName) {
                form.setFieldsValue({userName});
            }
        } else {
            message.error({
                content: "Vui lòng chọn phòng ban trước!"
            })
        }
    }

    const areaSelect = (
        <div className="mb-3">
            <p className="mb-1 ml-[2px] text-[16px] font-[400] text-primary">Khu vực quản lý</p>
            <TreeSelect
                showSearch
                allowClear
                treeCheckable
                placement="bottomLeft"
                filterTreeNode={(input, option) =>
                    normalizeVietnameseString((option?.label ?? '').toString()).toLowerCase().includes(normalizeVietnameseString(input).toLowerCase())
                }
                showCheckedStrategy="SHOW_PARENT"
                placeholder="Chọn Khu vực quản lý"
                style={{width: INPUT_WIDTH}}
                value={selectedAreas}
                treeData={areaTreeNodes}
                onChange={onSelectedAreasChange}>
            </TreeSelect>
        </div>
    )

    return (
        <ConfigProvider theme={MODAL_THEME}>
            <Modal open={isModalOpen} onCancel={handleCancel} width={isEdit?510:980} style={!isEdit?{ top: 20 }:{}} centered
                styles={{
                    mask: {
                        backdropFilter: 'blur(2px)',
                        backgroundColor: 'var(--primary-mask)'
                    }
                }}
                footer={[
                    <div key={1} className="w-full flex flex-row justify-end px-1.5">
                        <SaveButton isEdit={isEdit} loading={loading} onClick={handleSave}/>
                    </div>,
                ]}
            >
                <div className="flex flex-row w-full items-start justify-start select-none">
                    <p className="ml-2 text-2xl font-bold text-primary">CHI TIẾT BẢN GHI</p>
                </div>
                <div className="flex flex-row justify-center mt-6">
                    <Form<AppUserModel>
                        name="basic"
                        className="flex flex-col items-start"
                        autoComplete="off"
                        labelAlign="left"
                        layout="vertical"
                        disabled={isEdit&&!permissionStore.page.edit}
                        form={form}
                    >
                        {isEdit
                        ?<div>
                            <Form.Item<AppUserModel>
                                label="Tài khoản"
                                name="userName"
                                rules={[
                                    {
                                        required: true,
                                        message: 'Vui lòng tên tài khoản!',
                                    },
                                ]}
                            >
                                <Input placeholder="Nhập tài khoản" style={{width: INPUT_WIDTH}} disabled>
                            
                                </Input>
                            </Form.Item>
                            
                            <Form.Item<AppUserModel>
                                label="Phòng ban"
                                name="departmentId"
                                rules={!USERNAME_EDIT_ALLOWED?[{ required: true, message: 'Trường này là bắt buộc!' }]:undefined}
                            >
                                <Select placeholder="Chọn Phòng ban" style={{width: INPUT_WIDTH}}
                                options={[
                                    {value: undefined, label: 'Chọn Phòng ban'},
                                    ...departments.map(dep => ({value: dep.id, label: `${dep.code} - ${dep.name}`}))
                                ]}
                                filterOption={(input, option) =>
                                    (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                                }
                                />
                            </Form.Item>
                            {areaSelect}
                            <Form.Item<AppUserModel>
                                label="Họ và tên"
                                name="fullName"
                            >
                                <Input placeholder="Nhập họ và tên" style={{width: INPUT_WIDTH}}></Input>
                            </Form.Item>

                            <Form.Item<AppUserModel>
                                label="Ngày sinh"
                                name="birthOfDate"
                                getValueProps={(i) => ({ value: dayjsPipe(i) })}
                            >
                                <DatePicker format={DEF_DATE_FORMAT}style={{width: INPUT_WIDTH}}></DatePicker>
                            </Form.Item>
                            <Form.Item<AppUserModel>
                                label="CMND/CCCD"
                                name="cccd"
                            >
                                <Input placeholder="Nhập CMND/CCCD" style={{width: INPUT_WIDTH}}></Input>
                            </Form.Item>
                            <Form.Item<AppUserModel>
                                label="Chức vụ"
                                name="title"
                            >
                                <Input placeholder="Nhập Chức vụ" style={{width: INPUT_WIDTH}}></Input>
                            </Form.Item>
                            <Form.Item
                            name="isActive"
                            valuePropName="checked"
                            >
                                <Checkbox>
                                    Cho phép hoạt động
                                </Checkbox>
                            </Form.Item>
                        </div>
                        :<div className="grid grid-cols-2 gap-x-4">
                            <Form.Item<AppUserModel>
                                label="Tài khoản"
                                name="userName"
                                rules={[
                                    {
                                        required: true,
                                        message: 'Vui lòng tên tài khoản!',
                                    },
                                ]}
                            >
                                <Input placeholder="Nhập tài khoản" style={{width: INPUT_WIDTH}} disabled={!USERNAME_EDIT_ALLOWED}
                                    addonAfter={
                                        <Button type="text" style={{height: 30, color: "var(--primary-color)"}} icon={<SyncOutlined />} onClick={generateUsername}></Button>
                                    }>
                            
                                </Input>
                            </Form.Item>
                            
                            <Form.Item<AppUserModel>
                                label="Phòng ban"
                                name="departmentId"
                                rules={!USERNAME_EDIT_ALLOWED?[{ required: true, message: 'Trường này là bắt buộc!' }]:undefined}
                            >
                                <Select showSearch allowClear placeholder="Chọn Phòng ban" style={{width: INPUT_WIDTH}}
                                options={[
                                    {value: undefined, label: 'Chọn Phòng ban'},
                                    ...departments.map(dep => ({value: dep.id, label: `${dep.code} - ${dep.name}`}))
                                ]}
                                filterOption={(input, option) =>
                                    (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                                }
                                />
                            </Form.Item>
                                <Form.Item<AppUserModel>
                                    name="passwordHash"
                                    label="Mật khẩu"
                                    rules={[
                                    {
                                        required: true,
                                        message: 'Vui lòng nhập mật khẩu!',
                                    },
                                    ]}
                                >
                                    <Input.Password style={{width: INPUT_WIDTH}} placeholder="Nhập mật khẩu" />
                            </Form.Item>
                            <Form.Item
                                name="confirm"
                                label="Xác nhận mật khẩu"
                                dependencies={['password']}
                                hasFeedback
                                rules={[
                                {
                                    required: true,
                                    message: 'Vui lòng xác nhận mật khẩu!',
                                },
                                {
                                    validator: validatePasswords,
                                },
                                ]}
                            >
                                <Input.Password style={{width: INPUT_WIDTH}} placeholder="Xác nhận mật khẩu" />
                            </Form.Item>
                            <Form.Item<AppUserModel>
                                label="Họ và tên"
                                name="fullName"
                            >
                                <Input placeholder="Nhập họ và tên" style={{width: INPUT_WIDTH}}></Input>
                            </Form.Item>
                            <Form.Item<AppUserModel>
                                label="Ngày sinh"
                                name="birthOfDate"
                                getValueProps={(i) => ({ value: dayjsPipe(i) })}
                            >
                                <DatePicker format={DEF_DATE_FORMAT}style={{width: INPUT_WIDTH}}></DatePicker>
                            </Form.Item>
                            <Form.Item<AppUserModel>
                                label="CMND/CCCD"
                                name="cccd"
                            >
                                <Input placeholder="Nhập CMND/CCCD" style={{width: INPUT_WIDTH}}></Input>
                            </Form.Item>
                            <Form.Item<AppUserModel>
                                label="Chức vụ"
                                name="title"
                            >
                                <Input placeholder="Nhập Chức vụ" style={{width: INPUT_WIDTH}}></Input>
                            </Form.Item>
                            <Form.Item
                            name="isActive"
                            valuePropName="checked"
                            >
                                <Checkbox>
                                    Cho phép hoạt động
                                </Checkbox>
                            </Form.Item>
                            {areaSelect}
                        </div>}
                    </Form>
                </div>
                <div className="mb-5 mx-2 border-t border-dashed border-gray-300"></div>
            </Modal>
        </ConfigProvider>
    )
}
